import { GalleryControls } from '@app/components/various/Lightbox/GalleryControls';
import { VideoSlide } from '@app/components/various/Lightbox/VideoSlide';
import { getIsImageMedia, getIsVideoMedia } from '@app/services/media';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { animated, useSpring } from '@react-spring/web';
import { AnyLightboxMedia } from '@typings/internal';
import { useGesture } from '@use-gesture/react';
import React, { useEffect, useRef, useState } from 'react';

import { ImageSlide } from './ImageSlide';
import { LightboxThumbnails } from './LightboxThumbnails';

type GalleryProps = {
  medias: AnyLightboxMedia[];
  currentIndex: number;
  setIndex: (newIndex: number) => void;
};

const INITIAL_ZOOM_VALUE = 1;
const MAX_ZOOM_VALUE = 5;
const ZOOM_STEP = 1;

export const Gallery: React.FC<GalleryProps> = ({ medias, currentIndex, setIndex }) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const activeSlideRef = useRef<HTMLDivElement>(null);
  const [activeImageSize, setActiveImageSize] = useState({ height: 0, width: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [zoom, setZoom] = useState(INITIAL_ZOOM_VALUE);
  const currentMedia = medias[currentIndex];

  const isCurrentMediaVideoType = isDefined(currentMedia) && getIsVideoMedia(currentMedia);
  const [gallerySpring, galleryApi] = useSpring(() => ({ x: -window.innerWidth * currentIndex }));
  const [slideSpring, slideApi] = useSpring(() => ({
    px: 0,
    py: 0,
    zoom: INITIAL_ZOOM_VALUE,
  }));

  const isZoomedIn = zoom > INITIAL_ZOOM_VALUE;

  const handleDownload = () => {
    if (!currentMedia || !currentMedia.src) {
      return;
    }

    const link = document.createElement('a');
    // eslint-disable-next-line functional/immutable-data
    link.href = currentMedia.src;

    const filename = currentMedia.label || `image-${currentIndex + 1}`;
    link.setAttribute('download', filename);

    if (!getIsImageMedia(currentMedia)) {
      return;
    }

    fetch(currentMedia.src)
      .then(async response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        // eslint-disable-next-line functional/immutable-data
        link.href = url;
        link.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      })
      .catch(() => {
        // eslint-disable-next-line functional/immutable-data
        link.target = '_blank';
        link.click();
      });
  };

  const handleZoomInClick = () => {
    handleZoom({
      animate: true,
      newZoom: Math.min(MAX_ZOOM_VALUE, zoom + ZOOM_STEP),
    });
  };

  const handleZoomFill = () => {
    const scaleToFill = Math.max(window.innerWidth / activeImageSize.width, window.innerHeight / activeImageSize.height);
    handleZoom({
      animate: true,
      newZoom: scaleToFill,
    });
  };

  const handleZoomOutClick = () => {
    handleZoom({
      animate: true,
      newZoom: Math.max(INITIAL_ZOOM_VALUE, zoom - ZOOM_STEP),
    });
  };

  const handleZoomReset = () => {
    handleZoom({
      animate: true,
      newZoom: INITIAL_ZOOM_VALUE,
    });
  };

  const handleSwipe = (active: boolean, mx: number, vx: number) => {
    if (active) {
      galleryApi.set({ x: -currentIndex * window.innerWidth + mx });
    } else {
      const swipe = (Math.abs(mx) > 50 && vx > 0.2) || Math.abs(mx) > activeImageSize.width / 2;
      let newIndex = currentIndex;

      if (swipe) {
        const dir = mx > 0 ? -1 : 1;
        newIndex = Math.min(Math.max(currentIndex + dir, 0), medias.length - 1);
      }

      setIndex(newIndex);
      galleryApi.start({ x: -newIndex * window.innerWidth });
    }
  };

  const handleZoom = ({
    clientX,
    clientY,
    newZoom,
    animate = false,
  }: {
    clientX?: number;
    clientY?: number;
    newZoom: number;
    animate?: boolean;
  }) => {
    const container = activeSlideRef.current;
    const x = slideSpring.px.get();
    const y = slideSpring.py.get();
    if (!container || newZoom === zoom) return;

    const rect = container.getBoundingClientRect();

    const offsetX = isDefined(clientX) ? clientX - rect.left : rect.width / 2;
    const offsetY = isDefined(clientY) ? clientY - rect.top : rect.height / 2;

    const cx = offsetX - rect.width / 2;
    const cy = offsetY - rect.height / 2;

    const scaleRatio = newZoom / zoom;
    const { width, height } = activeImageSize;
    const imageW = width * newZoom;
    const imageH = height * newZoom;
    const viewW = rect.width;
    const viewH = rect.height;

    const maxPanX = Math.max((imageW - viewW) / 2, 0);
    const maxPanY = Math.max((imageH - viewH) / 2, 0);

    const newX = x - cx * (scaleRatio - 1);
    const newY = y - cy * (scaleRatio - 1);
    const clampedX = Math.max(-maxPanX, Math.min(maxPanX, newX));
    const clampedY = Math.max(-maxPanY, Math.min(maxPanY, newY));

    if (animate) {
      slideApi.start({ px: clampedX, py: clampedY, zoom: newZoom });
    } else {
      slideApi.set({ px: clampedX, py: clampedY, zoom: newZoom });
    }
    setZoom(newZoom);
  };

  const handlePan = (dx: number, dy: number) => {
    const container = activeSlideRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const viewW = rect.width;
    const viewH = rect.height;
    const { width, height } = activeImageSize;
    const imageW = width * zoom;
    const imageH = height * zoom;

    const maxPanX = Math.max((imageW - viewW) / 2, 0);
    const maxPanY = Math.max((imageH - viewH) / 2, 0);

    const newX = slideSpring.px.get() + dx;
    const newY = slideSpring.py.get() + dy;
    const clampedX = Math.max(-maxPanX, Math.min(maxPanX, newX));
    const clampedY = Math.max(-maxPanY, Math.min(maxPanY, newY));
    slideApi.set({ px: clampedX, py: clampedY });
  };

  useGesture(
    {
      onDrag: ({ active, delta: [dx, dy], movement: [mx], velocity: [vx], axis, cancel }) => {
        setIsDragging(active);

        if (zoom > 1) {
          handlePan(dx, dy);

          return;
        }

        if (axis === 'y') {
          cancel();

          return;
        }
        handleSwipe(active, mx, vx);
      },
      onPinch: ({ event, offset: [pinchScale] }) => {
        const wheelEvent = event as PointerEvent;
        const newZoom = Math.min(MAX_ZOOM_VALUE, Math.max(INITIAL_ZOOM_VALUE, pinchScale));
        handleZoom({ animate: true, clientX: wheelEvent.clientX ?? 0, clientY: wheelEvent.clientY ?? 0, newZoom });
      },
      onWheel: ({ event, delta: [dx, dy], metaKey }) => {
        if (metaKey) {
          const wheelEvent = event as WheelEvent;
          const delta = -dy * 0.01;
          const newZoom = Math.min(MAX_ZOOM_VALUE, Math.max(INITIAL_ZOOM_VALUE, zoom + delta));
          handleZoom({ clientX: wheelEvent.clientX, clientY: wheelEvent.clientY, newZoom });

          return false;
        }

        handlePan(dx, dy);

        return false;
      },
    },
    {
      drag: { filterTaps: true },
      eventOptions: { passive: false },
      pinch: { rubberband: true, scaleBounds: { max: MAX_ZOOM_VALUE, min: INITIAL_ZOOM_VALUE } },
      target: galleryRef,
    },
  );

  useEffect(() => {
    slideApi.set({ px: 0, py: 0, zoom: INITIAL_ZOOM_VALUE });
    galleryApi.start({ x: -currentIndex * window.innerWidth });
  }, [currentIndex, slideApi, galleryApi]);

  useEffect(() => {
    const handleResize = (alignGallery: boolean) => () => {
      if (alignGallery) {
        galleryApi.set({ x: -window.innerWidth * currentIndex });
      }

      if (imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        setActiveImageSize({ height: rect.height, width: rect.width });
      }
    };
    const resizeHandler = handleResize(true);
    window.addEventListener('resize', resizeHandler);
    handleResize(false)();

    return () => window.removeEventListener('resize', resizeHandler);
  }, [currentIndex, galleryApi]);

  return (
    <>
      <div className="h-screen w-full overflow-hidden bg-white">
        {!isCurrentMediaVideoType && (
          <GalleryControls
            onZoomIn={handleZoomInClick}
            onZoomOut={handleZoomOutClick}
            onZoomReset={handleZoomReset}
            onDownload={handleDownload}
            onZoomFill={handleZoomFill}
            zoom={zoom}
            isZoomInDisabled={zoom >= MAX_ZOOM_VALUE}
            isZoomOutDisabled={zoom <= INITIAL_ZOOM_VALUE}
          />
        )}
        <animated.div
          ref={galleryRef}
          className="flex h-full w-full touch-none"
          style={{
            touchAction: 'none',
            transform: gallerySpring.x.to(val => `translateX(${val}px)`),
          }}
        >
          {medias.map((media, index) => (
            <div
              key={index}
              className={cn('h-full w-full flex-shrink-0 overflow-hidden', {
                'cursor-grab': isZoomedIn && !isDragging,
                'cursor-grabbing': isZoomedIn && isDragging,
              })}
              ref={currentIndex === index ? activeSlideRef : undefined}
            >
              {getIsImageMedia(media) ?
                <ImageSlide
                  ref={imageRef}
                  src={media.src}
                  alt={media.title}
                  isPlaceholder={media.isPlaceholder}
                  isActive={currentIndex === index}
                  slideSpring={slideSpring}
                />
              : <VideoSlide videoId={media.src ?? ''} isActive={currentIndex === index} />}
            </div>
          ))}
        </animated.div>
      </div>
      {!isCurrentMediaVideoType && <LightboxThumbnails medias={medias} onChange={setIndex} currentIndex={currentIndex} />}
    </>
  );
};
